import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setMailRead } from '@/services/api/endpoints';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { reportError } from '@/services/errors/errorReporter';
import type { MailSummary, SearchResponse } from '@/types/domain';

/**
 * Bascule l'état « lu par l'utilisateur courant » d'un mail, et l'affiche AUSSITÔT.
 *
 * OPTIMISTE, et ce n'est pas de la coquetterie : ouvrir un mail lance déjà le
 * téléchargement de son `.msg`, qui peut prendre plusieurs secondes. Attendre la
 * confirmation du serveur pour éteindre la pastille laisserait le mail « non lu »
 * pendant tout ce temps, alors qu'il s'ouvre sous les yeux de l'utilisateur — il
 * cliquerait une seconde fois. Le sens inverse (« marquer comme non lu ») n'a même
 * pas cette excuse : c'est un clic dans un menu, dont la seule issue VISIBLE est
 * la pastille. La différer serait la faire paraître sans effet.
 *
 * PAS DE MUTATION TanStack ici, à dessein : une mutation par ligne obligerait à
 * porter un état par mail dans une grille qui en compte des centaines, alors qu'il
 * n'y a rien à attendre ni à annuler. L'appel est « tire et oublie ».
 *
 * En cas d'échec, on n'annule PAS l'affichage — on invalide, et le prochain
 * chargement dira la vérité. Remettre la pastille séance tenante ferait clignoter
 * un mail que l'utilisateur est justement en train de lire.
 *
 * DEUX ARGUMENTS ET NON UN, alors qu'un seul suffisait à l'usage d'origine
 * (« ouvrir » ⇒ « lu ») : un `markRead(id)` sous-entendait qu'il n'existe qu'un
 * sens, et le menu contextuel a besoin de l'autre. Un booléen explicite dit à
 * chaque appel ce qui est affirmé, plutôt que de le laisser deviner au nom.
 */
export function useSetMailRead(): (mailId: string, read: boolean) => void {
  const qc = useQueryClient();

  return useCallback(
    (mailId: string, read: boolean) => {
      // Les deux listes de mails — dossier (`['mails', folderId]`) et racine de
      // projet (`['mails', 'root', containerId]`) — sont couvertes par le préfixe
      // commun. Un mail visible dans plusieurs vues change donc partout.
      qc.setQueriesData<MailSummary[]>({ queryKey: ['mails'] }, (rows) =>
        rows?.some((r) => r.id === mailId)
          ? rows.map((r) => (r.id === mailId ? { ...r, isRead: read } : r))
          : rows,
      );
      // La recherche garde ses propres résultats, sous une autre clé : sans ceci,
      // ouvrir un mail depuis les résultats laisserait sa pastille allumée.
      qc.setQueriesData<SearchResponse>({ queryKey: ['search'] }, (resp) =>
        resp?.mails?.some((m) => m.id === mailId)
          ? {
              ...resp,
              mails: resp.mails.map((m) => (m.id === mailId ? { ...m, isRead: read } : m)),
            }
          : resp,
      );

      addBreadcrumb({ category: 'mutation', message: 'set-mail-read', data: { mailId, read } });
      void setMailRead(mailId, read).catch((err) => {
        reportError({
          error: err instanceof Error ? err : new Error(String(err)),
          severity: 'warning',
          isHandled: true,
          tags: ['mail', read ? 'mark-read' : 'mark-unread'],
        });
        void qc.invalidateQueries({ queryKey: ['mails'] });
      });
    },
    [qc],
  );
}
